import type { AuthService } from "./authService";
import { config } from "../application/common/static-config";

class BaseApiService {
  private baseURL: string;
  private authServiceInstance: AuthService | null = null;
  private isRefreshingToken = false;
  private tokenRefreshPromise: Promise<string> | null = null;

  constructor() {
    this.baseURL = config.baseURL;
  }

  // Method to set the AuthService instance
  setAuthService(authService: AuthService) {
    this.authServiceInstance = authService;
  }

  private getAuthToken(): string | null {
    return (
      localStorage.getItem("bestekJwtToken") ||
      sessionStorage.getItem("bestekJwtToken")
    );
  }

  private getHeaders(includeAuth: boolean = true): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (includeAuth) {
      const token = this.getAuthToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    return headers;
  }

    private async refreshToken(): Promise<string> {
    // If a refresh is already in progress, wait for it
    if (this.isRefreshingToken && this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }

    this.isRefreshingToken = true;
    this.tokenRefreshPromise = (async () => {
      try {
        if (!this.authServiceInstance) {
          throw new Error("Auth service not initialized");
        }

        const newToken = await this.authServiceInstance.exchangeTokenWithBackend();
        localStorage.setItem("bestekJwtToken", newToken);
        sessionStorage.removeItem("recent-401-error"); // Clear 401 error flag on successful refresh
        return newToken;
      } catch (error) {
        // If refresh fails, trigger logout
        console.error("BaseApiService: Token refresh failed, logging out:", error);
        localStorage.clear();
        sessionStorage.clear();

        if (this.authServiceInstance) {
          try {
            await this.authServiceInstance.logout();
          } catch (logoutError) {
            console.warn("BaseApiService: Logout failed:", logoutError);
          }
        }

        window.location.href = "/login";
        throw error;
      } finally {
        this.isRefreshingToken = false;
        this.tokenRefreshPromise = null;
      }
    })();

    return this.tokenRefreshPromise;
  }

  private async handleResponse<T>(
    response: Response,
    retryFn?: () => Promise<Response>,
    parseAs: "json" | "blob" = "json"
  ): Promise<T> {
    if (!response.ok) {
      if (response.status === 401) {
        if (retryFn) {
          try {
            console.log("BaseApiService: Received 401, attempting token refresh...");
            await this.refreshToken();

            // Retry the original request with the new token
            const retryResponse = await retryFn();
            return this.handleResponse<T>(retryResponse, undefined, parseAs); // Recursive call without retryFn to prevent infinite loop
          } catch (refreshError) {
            console.error("BaseApiService: Token refresh failed:", refreshError);
            throw new Error("Authentication failed - please login again");
          }
        }
        // Check if backend is unavailable
        const backendUnavailable =
          sessionStorage.getItem("backend-unavailable") === "true";

        if (backendUnavailable) {
          throw new Error("API unavailable - backend not accessible");
        }

        // Add a flag to track 401 errors and prevent immediate logout on first occurrence
        const recent401 = sessionStorage.getItem("recent-401-error");
        const currentTime = Date.now();

        if (recent401) {
          const last401Time = parseInt(recent401);
          const timeSince401 = currentTime - last401Time;

          // If we got another 401 within 30 seconds, it's likely a real auth issue
          if (timeSince401 < 30000) {
            console.warn(
              "BaseApiService: Multiple 401s detected, triggering logout"
            );

            // Token expired or invalid, clear all storage and redirect to login
            localStorage.clear();
            sessionStorage.clear();

            // Call MSAL logout if available
            if (this.authServiceInstance) {
              try {
                await this.authServiceInstance.logout();
              } catch (error) {
                console.warn("MSAL logout failed:", error);
              }
            }

            window.location.href = "/login";
            throw new Error("Authentication expired");
          }
        }

        // First 401 - just mark it and continue
        sessionStorage.setItem("recent-401-error", currentTime.toString());
        throw new Error(
          "Authentication token may be expired - please try again"
        );
      }

      let errorMessage = `API Error: ${response.status}`;

      try {
        const errorData = await response.text();
        const errorJson = JSON.parse(errorData);

        // Extract detail or message from JSON response
        if (errorJson.detail) {
          errorMessage = errorJson.detail;
        } else if (errorJson.message) {
          errorMessage = errorJson.message;
        } else {
          errorMessage = `API Error: ${response.status} - ${errorData}`;
        }
      } catch {
        // If parsing fails, keep the default error message
        errorMessage = `API Error: ${response.status}`;
      }

      throw new Error(errorMessage);
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return {} as T;
    }

    return (parseAs === "blob" ? response.blob() : response.json()) as Promise<T>;
  }

  async getBlob(endpoint: string): Promise<Blob> {
    const retryFn = () =>
      fetch(`${this.baseURL}${endpoint}`, {
        method: "GET",
        headers: this.getHeaders(),
      });
    const response = await retryFn();
    return this.handleResponse<Blob>(response, retryFn, "blob");
  }

  async get<T>(endpoint: string): Promise<T> {
    const retryFn = () =>
      fetch(`${this.baseURL}${endpoint}`, {
      method: "GET",
      headers: this.getHeaders(),
    });
    const response = await retryFn();
    return this.handleResponse<T>(response, retryFn);
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    const retryFn = () =>
      fetch(`${this.baseURL}${endpoint}`, {
      method: "POST",
      headers: this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });
    const response = await retryFn();
    return this.handleResponse<T>(response, retryFn);
  }

  async postWithTimeout<T>(
    endpoint: string,
    data?: unknown,
    timeoutMs: number = 600000
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const retryFn = () =>
      fetch(`${this.baseURL}${endpoint}`, {
        method: "POST",
        headers: this.getHeaders(),
        body: data ? JSON.stringify(data) : undefined,
        signal: controller.signal,
      });
    try {
      const response = await retryFn();
      clearTimeout(timeoutId);
      return this.handleResponse<T>(response, retryFn);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new Error(`Request timeout after ${timeoutMs / 1000} seconds`);
      }
      throw error;
    }
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    const retryFn = () =>
      fetch(`${this.baseURL}${endpoint}`, {
      method: "PUT",
      headers: this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });
    const response = await retryFn();
    return this.handleResponse<T>(response, retryFn);
  }

  async putWithTimeout<T>(
    endpoint: string,
    data?: unknown,
    timeoutMs: number = 600000
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const retryFn = () =>
      fetch(`${this.baseURL}${endpoint}`, {
        method: "PUT",
        headers: this.getHeaders(),
        body: data ? JSON.stringify(data) : undefined,
        signal: controller.signal,
      });

    try {
      const response = await retryFn();
      clearTimeout(timeoutId);
      return this.handleResponse<T>(response, retryFn);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new Error(`Request timeout after ${timeoutMs / 1000} seconds`);
      }
      throw error;
    }
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    const retryFn = () =>
      fetch(`${this.baseURL}${endpoint}`, {
      method: "PATCH",
      headers: this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });
    const response = await retryFn();
    return this.handleResponse<T>(response, retryFn);
  }

  async delete<T>(endpoint: string): Promise<T> {
    const retryFn = () =>
      fetch(`${this.baseURL}${endpoint}`, {
      method: "DELETE",
      headers: this.getHeaders(),
    });
    const response = await retryFn();
    return this.handleResponse<T>(response, retryFn);
  }

  // Upload files with auth
  async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, string>
  ): Promise<T> {
    const formData = new FormData();
    formData.append("file", file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const token = this.getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const retryFn = () =>
      fetch(`${this.baseURL}${endpoint}`, {
      method: "POST",
      headers,
      body: formData,
    });
    const response = await retryFn();

    return this.handleResponse<T>(response, retryFn);
  }
}

// Export singleton instance
export const baseApiService = new BaseApiService();

// Common utility functions
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

// Helper function to get MIME type from file extension
export const getContentType = (fileName: string): string => {
  const extension = fileName.toLowerCase().split(".").pop();
  switch (extension) {
    case "pdf":
      return "application/pdf";
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "xlsx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    default:
      return "application/octet-stream";
  }
};
